import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { CreditPurchaseCreateStore } from "../../store";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { TenantCreate } from "../../../tenant/components/create/TenantCreate";
import { TenantCreateStore } from "../../../tenant/store";
import { InputNumber } from "../../../common/controls";
import { InputDate } from "../../../common/controls";
import { InputString } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type CreditPurchaseCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  tenantSelectedValue?: { displayId: string; id: string } | null;
  onTenantSelected?: (value: { displayId: string; id: string } | null) => void;
  onTenantChange?: (value: string) => void;
  tenantOptions?: { displayId: string; id: string }[];
  tenantError?: string;
  quantitySelectedValue?: { displayId: string; id: string } | null;
  onQuantityChange?: (value: number) => void;
  quantity: number;
  quantityError?: string;
  purchaseDateSelectedValue?: { displayId: string; id: string } | null;
  onPurchaseDateChange?: (value: string | null) => void;
  purchaseDate: string | null;
  purchaseDateError?: string;
  expirationDateSelectedValue?: { displayId: string; id: string } | null;
  onExpirationDateChange?: (value: string | null) => void;
  expirationDate: string | null;
  expirationDateError?: string;
  unitPriceSelectedValue?: { displayId: string; id: string } | null;
  onUnitPriceChange?: (value: number | undefined) => void;
  unitPrice: number | undefined;
  unitPriceError?: string;
  notesSelectedValue?: { displayId: string; id: string } | null;
  onNotesChange?: (value: string) => void;
  notes: string;
  notesError?: string;
  creditPurchaseCreateStore: CreditPurchaseCreateStore;
};

export const CreditPurchaseCreateForm: React.FC<CreditPurchaseCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      onTenantSelected = () => {},
      tenantSelectedValue = null,
      tenantOptions = [],
      tenantError,
      onTenantChange = () => {},
      quantity = 0,
      quantityError,
      onQuantityChange = () => {},
      purchaseDate = null,
      purchaseDateError,
      onPurchaseDateChange = () => {},
      expirationDate = null,
      expirationDateError,
      onExpirationDateChange = () => {},
      unitPrice = undefined,
      unitPriceError,
      onUnitPriceChange = () => {},
      notes = "",
      notesError,
      onNotesChange = () => {},
      creditPurchaseCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      const tenantIsVisible = ["SysAdmin"].some((role) =>
        rootStore.authStore.hasRole(role)
      );
      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="CreditPurchase"
              mode="create"
              values={{}}
              options={{ tenant: tenantOptions }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "tenant":
                    onTenantSelected?.(valor);
                    break;
                  case "quantity":
                    onQuantityChange?.(valor);
                    break;
                  case "purchaseDate":
                    onPurchaseDateChange?.(valor);
                    break;
                  case "expirationDate":
                    onExpirationDateChange?.(valor);
                    break;
                  case "unitPrice":
                    onUnitPriceChange?.(valor);
                    break;
                  case "notes":
                    onNotesChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={creditPurchaseCreateStore.getCardErrors("null")}
              title={langStore.groupCreditPurchaseLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                {tenantIsVisible && (
                  <InputReference
                    id="tenant"
                    label={langStore.CreditPurchaseTenantLabel}
                    autoFocus
                    error={Boolean(tenantError)}
                    helperText={
                      tenantError ||
                      creditPurchaseCreateStore.warnings["tenant"]?.message
                    }
                    selectedValue={tenantSelectedValue}
                    onChange={(value: string) => onTenantChange(value)}
                    onSelect={(
                      value: { displayId: string; id: string } | null
                    ) => onTenantSelected(value)}
                    options={tenantOptions}
                    description={langStore.CreditPurchaseTenantDescription}
                    nullText={langStore.optionalFieldHint}
                    canCreate={rootStore.authStore.hasPermissions(
                      "tenant_create"
                    )}
                    renderCreateDialog={(dialogProps) => (
                      <CreateEntityDialog
                        {...dialogProps}
                        createStore={(rootStore) =>
                          new TenantCreateStore(rootStore)
                        }
                        renderForm={(store) => (
                          <TenantCreate
                            tenantCreateStore={store}
                            showToolbox={false}
                            showSaveFooter={false}
                          />
                        )}
                        saveMethod="saveNewTenant"
                        cancelMethod="cancelNewTenant"
                      />
                    )}
                  />
                )}
                <InputNumber
                  value={quantity}
                  error={Boolean(quantityError)}
                  warning={
                    !quantityError &&
                    Boolean(creditPurchaseCreateStore.warnings["quantity"])
                  }
                  helperText={
                    quantityError ||
                    creditPurchaseCreateStore.warnings["quantity"]?.message
                  }
                  allowNegative={false}
                  label={langStore.CreditPurchaseQuantityLabel}
                  decimalScale={0}
                  description={langStore.CreditPurchaseQuantityDescription}
                  onChange={(value: number) => onQuantityChange(value)}
                />
                <InputDate
                  value={purchaseDate}
                  error={Boolean(purchaseDateError)}
                  warning={
                    !purchaseDateError &&
                    Boolean(creditPurchaseCreateStore.warnings["purchaseDate"])
                  }
                  helperText={
                    purchaseDateError ||
                    creditPurchaseCreateStore.warnings["purchaseDate"]?.message
                  }
                  label={langStore.CreditPurchasePurchaseDateLabel}
                  description={langStore.CreditPurchasePurchaseDateDescription}
                  onChange={(value: string | null | null) =>
                    onPurchaseDateChange(value)
                  }
                />
                <InputDate
                  value={expirationDate}
                  error={Boolean(expirationDateError)}
                  warning={
                    !expirationDateError &&
                    Boolean(
                      creditPurchaseCreateStore.warnings["expirationDate"]
                    )
                  }
                  helperText={
                    expirationDateError ||
                    creditPurchaseCreateStore.warnings["expirationDate"]
                      ?.message
                  }
                  label={langStore.CreditPurchaseExpirationDateLabel}
                  nullText={langStore.optionalFieldHint}
                  description={
                    langStore.CreditPurchaseExpirationDateDescription
                  }
                  onChange={(value: string | null | null) =>
                    onExpirationDateChange(value)
                  }
                />
                <InputNumber
                  value={unitPrice}
                  nullable={true}
                  error={Boolean(unitPriceError)}
                  warning={
                    !unitPriceError &&
                    Boolean(creditPurchaseCreateStore.warnings["unitPrice"])
                  }
                  helperText={
                    unitPriceError ||
                    creditPurchaseCreateStore.warnings["unitPrice"]?.message
                  }
                  allowNegative={true}
                  label={langStore.CreditPurchaseUnitPriceLabel}
                  decimalScale={2}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.CreditPurchaseUnitPriceDescription}
                  onChange={(value: number | undefined) =>
                    onUnitPriceChange(value)
                  }
                />

                <InputString
                  value={notes}
                  error={Boolean(notesError)}
                  warning={
                    !notesError &&
                    Boolean(creditPurchaseCreateStore.warnings["notes"])
                  }
                  helperText={
                    notesError ||
                    creditPurchaseCreateStore.warnings["notes"]?.message
                  }
                  label={langStore.CreditPurchaseNotesLabel}
                  onChange={(value: string) => onNotesChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.CreditPurchaseNotesDescription}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
